import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import goalService, {
  type CreateGoalDto,
  type UpdateGoalDto,
  type GoalImportExecutePayload,
} from '../services/api/goalService';

/** Full Domain -> Program -> Goal/Target tree for the Goals management page. */
export function useGoalTree(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'goals', clientId],
    queryFn: () => goalService.getGoalTree(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });
}

export function useCreateGoal(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateGoalDto) => goalService.createGoal(clientId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'goals', clientId] });
    },
  });
}

export function useUpdateGoal(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, dto }: { goalId: string; dto: UpdateGoalDto }) =>
      goalService.updateGoal(goalId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'goals', clientId] });
    },
  });
}

export function useDeleteGoal(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (goalId: string) => goalService.deleteGoal(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'goals', clientId] });
    },
  });
}

/** Dry-run validate an import CSV — does not write anything. */
export function useGoalImportValidate() {
  return useMutation({
    mutationFn: ({ file, clientId }: { file: File; clientId?: string }) =>
      goalService.importValidate(file, clientId),
  });
}

/** Confirm an import — invalidates the affected client's tree (single-client) or the
 * whole goals cache (bulk/onboarding import, unknown clientId set). */
export function useGoalImportExecute(clientId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: GoalImportExecutePayload) => goalService.importExecute(payload),
    onSuccess: () => {
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: ['clients', 'goals', clientId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['clients', 'goals'] });
      }
    },
  });
}
